/*
 * PageHeader: the top of every page. Title (also the tab title), description, actions on the end side,
 * optional breadcrumbs, a back link and meta (pills, badges) under the title.
 *   <PageHeader
 *     title={t('servers:title')}
 *     description={t('servers:description')}
 *     breadcrumbs={[{ label: t('shell:nav.servers'), to: '/servers' }, { label: server.name }]}
 *     actions={<Button icon={<Plus />} asChild><Link to="/servers/new">…</Link></Button>}
 *   />
 */
import { ArrowRight, CaretLeft } from '@phosphor-icons/react'
import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { useDocumentTitle } from '@/lib/useDocumentTitle'

export type Crumb = { label: ReactNode; to?: string }

export function Breadcrumbs({ items, className }: { items: Crumb[]; className?: string }) {
  const { t } = useTranslation('common')
  return (
    <nav aria-label={t('ui.breadcrumbs')} className={className}>
      <ol className="flex flex-wrap items-center gap-1 text-13 text-subtle">
        {items.map((c, i) => (
          <Fragment key={i}>
            <li className="min-w-0">
              {c.to && i < items.length - 1 ? (
                <Link to={c.to} className="truncate rounded-md px-0.5 transition-colors hover:text-ink">
                  {c.label}
                </Link>
              ) : (
                <span
                  aria-current={i === items.length - 1 ? 'page' : undefined}
                  className="truncate text-muted"
                >
                  {c.label}
                </span>
              )}
            </li>
            {i < items.length - 1 ? (
              <li aria-hidden="true">
                <CaretLeft className="size-3 flip-ltr" />
              </li>
            ) : null}
          </Fragment>
        ))}
      </ol>
    </nav>
  )
}

export function PageHeader({
  title,
  description,
  actions,
  breadcrumbs,
  back,
  meta,
  documentTitle,
  className,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  breadcrumbs?: Crumb[]
  /** a back link above the title: { to, label } */
  back?: { to: string; label: ReactNode }
  /** pills or badges after the title */
  meta?: ReactNode
  /** tab title when `title` is not a plain string */
  documentTitle?: string
  className?: string
}) {
  useDocumentTitle(documentTitle ?? (typeof title === 'string' ? title : undefined))
  return (
    <header className={cn('flex flex-col gap-3 pb-6 sm:pb-8', className)}>
      {breadcrumbs?.length ? <Breadcrumbs items={breadcrumbs} /> : null}
      {back ? (
        <Link
          to={back.to}
          className="inline-flex w-fit items-center gap-1.5 rounded-md text-13 font-medium text-muted transition-colors hover:text-ink"
        >
          <ArrowRight className="size-4 flip-ltr" aria-hidden="true" />
          {back.label}
        </Link>
      ) : null}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="display text-24 sm:text-28">{title}</h1>
            {meta}
          </div>
          {description ? <p className="max-w-2xl text-15 leading-6 text-muted">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  )
}
